import { FC } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from 'jbc-front/components/Button';
import { Label, Required } from 'jbc-front/components/presenters/form/Label';
import { Spacer } from 'jbc-front/components/presenters/Spacer';
import { Input } from 'jbc-front/components/presenters/form/Input';
import { ErrorMessage } from 'jbc-front/components/presenters/form/ErrorMessage';
import { Content } from '@/components/layout/Content';
import { Headding, Item, Panel } from '@/components/PrimaryPanel';
import { useSession } from '@/components/SessionProvider';
import { FormErrors } from '@/components/form/FormErrors';
import { useMutation } from '@/hooks/graphql/useMutation';
import { useNotify } from '@/hooks/useNotify';
import { parseBackUrl } from '@/utils/url';
import { PermissionForm } from '../edit/parts/PermissionForm/PermissionForm';
import { RoleProhibit } from '../edit/parts/PermissionForm/roleOptions';
import { ADD_NEW_USER, AddNewUserQueryVariables } from './query';
import { Schema, schema } from './schema';
import styles from './CreateUser.scss';

export const CreateUser: FC = () => {
  const { user: currentUser } = useSession();
  const notify = useNotify();

  const location = useLocation();
  const history = useHistory();

  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
    context: { currentUser }
  });
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = methods;

  const [addNewUser] = useMutation<unknown, AddNewUserQueryVariables>(ADD_NEW_USER);

  const onSubmit: SubmitHandler<Schema> = async data => {
    const roleCodes = [data.stressCheckRole, data.healthCheckRole, data.userManageRole].filter(
      (role): role is string => role !== undefined && role !== null && role !== RoleProhibit
    );
    if (roleCodes.length === 0) {
      setError('root.roleCodes', {
        type: 'manual',
        message: 'すべての権限を「権限なし」にはできません。少なくとも1つ以上の権限を「権限あり」に設定してください。'
      });
      return;
    }

    await addNewUser({
      variables: {
        input: {
          email: data.email,
          name: data.name,
          roleCodes
        }
      }
    });

    notify('招待メールを送信しました', 'success');
    history.push(parseBackUrl(location, '/settings/users'));
  };

  return (
    <>
      <div className="l-title-wrap">
        <h1 className="m-title-main">新規管理者を追加</h1>
      </div>
      <Content size="xs">
        <FormProvider {...methods}>
          {errors.root?.roleCodes?.message && (
            <FormErrors errors={[{ path: 'roleCodes', message: errors.root.roleCodes.message }]} />
          )}

          <Panel>
            <Headding>新規管理者を追加</Headding>
            <Item>
              <div className={styles.field}>
                <Label>
                  メールアドレス
                  <Spacer direction="x" size={8} />
                  <Required />
                </Label>
                <Spacer direction="y" size={8} />
                <Input {...register('email')} />
                {errors.email?.message && <ErrorMessage>{errors.email.message}</ErrorMessage>}
              </div>
              <div className={styles.field}>
                <Label>氏名</Label>
                <Spacer direction="y" size={8} />
                <Input {...register('name')} />
                {errors.name?.message && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </div>
              <PermissionForm />
            </Item>
          </Panel>

          <div className={styles.footer}>
            <Button as={Link} to={parseBackUrl(location, '/settings/users')}>
              キャンセル
            </Button>
            <Button primary onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
              追加
            </Button>
          </div>
        </FormProvider>
      </Content>
    </>
  );
};
